import type { AccessPolicy, Resource } from '@medplum/fhirtypes';
import { createContext, useContext } from 'react';

import { computeFieldAccess } from '../resolvers/field-state';

import type {
  RenderContext,
  RenderMode,
  RenderPlacement,
  RenderSurface,
} from './view-contract';

const ReactRenderContext = createContext<RenderContext | undefined>(undefined);

export const RenderContextProvider = ReactRenderContext.Provider;

export function useRenderContext(): RenderContext {
  const ctx = useContext(ReactRenderContext);
  if (!ctx) {
    throw new Error(
      '[fhir-renderer] useRenderContext must be used inside <FhirRenderRoot>',
    );
  }
  return ctx;
}

export function rootRenderContext(
  options: {
    surface?: RenderSurface;
    placement?: RenderPlacement;
    mode?: RenderMode;
    debug?: boolean;
  } = {},
): RenderContext {
  return {
    parents: [],
    inList: false,
    surface: options.surface ?? 'page',
    placement: options.placement ?? 'main',
    mode: options.mode ?? 'read',
    debug: options.debug ?? false,
  };
}

/**
 * Context the resource's own view renders with: parent chain unchanged
 * (the resource is not its own parent), access stamped for this resource,
 * list placement as reported by the parent's slot.
 */
export function viewRenderContext(input: {
  parent: RenderContext;
  resource: Resource;
  accessPolicy: AccessPolicy | undefined;
  inList: boolean;
}): RenderContext {
  return {
    parent: input.parent.parents[0]?.resourceType,
    parents: input.parent.parents,
    inList: input.inList,
    surface: input.parent.surface,
    placement: input.parent.placement,
    mode: input.parent.mode,
    debug: input.parent.debug,
    access: computeFieldAccess(input.resource, input.accessPolicy),
  };
}

/**
 * Context for a node's children: pushes the current resource onto the
 * parent chain. Slots are built against this, so descendants see the chain
 * and cycle checks catch self-references.
 */
export function childRenderContext(
  parent: RenderContext,
  resource: Resource,
  accessPolicy?: AccessPolicy,
): RenderContext {
  const parents = [
    { resourceType: resource.resourceType, id: resource.id },
    ...parent.parents,
  ];
  return {
    parent: resource.resourceType,
    parents,
    inList: false,
    surface: parent.surface,
    placement: parent.placement,
    mode: parent.mode,
    debug: parent.debug,
    // Stamped for the resource being rendered: <Field> inside the view reads
    // this context (the provider wraps the view with the child ctx).
    access: computeFieldAccess(resource, accessPolicy),
  };
}

export function isInParentChain(
  ctx: RenderContext,
  resource: Resource,
): boolean {
  return ctx.parents.some(
    (p) =>
      p.resourceType === resource.resourceType &&
      p.id !== undefined &&
      p.id === resource.id,
  );
}
